/*
 * The portal: one full-screen fragment shader over plain WebGL. No three.js: everything here is a single quad, so
 * a 3D library would only add bundle weight to the app.
 *
 * The "wavetable" is a texture of band levels over time. Every ring of the portal reads one row of it, so a change
 * in the sound appears on the inner ring and ripples outward.
 */
const ROWS = 32;

const VERTEX = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAGMENT = `
precision highp float;
uniform vec2 uRes;
uniform vec2 uCenter;
uniform float uScale;
uniform float uTime;
uniform float uEnergy;
uniform sampler2D uTable;

const float PI = 3.14159265;
const int LAYERS = 24;

vec3 palette(float t) {
    return 0.55 + 0.45 * cos(6.28318 * (t + vec3(0.0, 0.33, 0.62)));
}

float noise(vec2 p) {
    return 0.5 + 0.25 * (sin(p.x * 2.1 + sin(p.y * 1.7)) + sin(p.y * 2.7 + sin(p.x * 1.3)));
}

void main() {
    vec2 p = (gl_FragCoord.xy - uCenter) / uScale;
    float r = length(p);
    float a = atan(p.y, p.x);
    // Mirrored, so low notes meet on one side and high notes on the other with no seam.
    float m = abs(a) / PI;

    vec3 col = vec3(0.012, 0.008, 0.03);

    float haze = noise(vec2(m * 7.0 + uTime * 0.05, r * 3.0 - uTime * 0.14));
    float halo = exp(-abs(r - 0.72) * 2.2);
    col += palette(m * 0.5 + r * 0.25 + uTime * 0.02) * halo * haze * (0.1 + 0.4 * uEnergy);

    for (int i = 0; i < LAYERS; i++) {
        float k = float(i) / float(LAYERS - 1);
        float level = texture2D(uTable, vec2(0.01 + m * 0.98, k)).r;
        float wobble = sin(a * 3.0 + uTime * 0.31 + k * 6.0) * 0.018
            + sin(a * 7.0 - uTime * 0.23 + k * 9.0) * 0.008;
        float radius = 0.62 + k * 0.72 + level * (0.05 + 0.3 * k) + wobble;
        float d = abs(r - radius);
        float glow = (0.0012 + 0.0032 * level) / (d + 0.0028);
        float fade = pow(1.0 - k, 1.3);
        col += palette(m * 0.65 + k * 0.8 + uTime * 0.025) * glow * fade;
    }

    // The mouth of the portal stays dark, so the time written over it can be read.
    col *= smoothstep(0.3, 0.64, r);

    gl_FragColor = vec4(1.0 - exp(-col * 1.25), 1.0);
}
`;

export interface PortalFrame {
    /** Portal center in CSS pixels from the top left of the canvas, and the radius that counts as 1. */
    centerX: number;
    centerY: number;
    radius: number;
    seconds: number;
    energy: number;
}

export interface PortalRenderer {
    /** Pushes one row of band levels (0..1) into the wavetable. */
    push: (bands: Float32Array) => void;
    draw: (frame: PortalFrame) => void;
    dispose: () => void;
}

/** Never more than this many pixels: a sleep app should not cook the phone it is lying next to. */
const MAX_PIXELS = 1_400_000;

const compile = (
    gl: WebGLRenderingContext,
    type: number,
    source: string
): WebGLShader | null => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
};

/** Null when WebGL is missing or refuses the shader; the page then keeps its plain CSS glow. */
export const createPortalRenderer = (
    canvas: HTMLCanvasElement,
    bands: number
): PortalRenderer | null => {
    const gl = canvas.getContext?.('webgl', {
        alpha: false,
        antialias: false,
        powerPreference: 'low-power',
    });
    if (!gl) return null;
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) return null;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    gl.useProgram(program);

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
        gl.STATIC_DRAW
    );
    const position = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const table = new Uint8Array(bands * ROWS);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const upload = () =>
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.LUMINANCE,
            bands,
            ROWS,
            0,
            gl.LUMINANCE,
            gl.UNSIGNED_BYTE,
            table
        );
    upload();

    const uniform = (name: string) => gl.getUniformLocation(program, name);
    const uRes = uniform('uRes');
    const uCenter = uniform('uCenter');
    const uScale = uniform('uScale');
    const uTime = uniform('uTime');
    const uEnergy = uniform('uEnergy');

    return {
        push: (row) => {
            // Row 0 is the newest (the inner ring); older rows move outward.
            table.copyWithin(bands, 0, bands * (ROWS - 1));
            for (let i = 0; i < bands; i++) {
                table[i] = Math.round(Math.min(1, row[i]) * 255);
            }
            upload();
        },
        draw: ({ centerX, centerY, radius, seconds, energy }) => {
            const cssWidth = canvas.clientWidth || 1;
            const cssHeight = canvas.clientHeight || 1;
            const ratio = Math.min(
                window.devicePixelRatio || 1,
                Math.sqrt(MAX_PIXELS / (cssWidth * cssHeight))
            );
            const width = Math.round(cssWidth * ratio);
            const height = Math.round(cssHeight * ratio);
            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
            }
            gl.viewport(0, 0, width, height);
            gl.uniform2f(uRes, width, height);
            // WebGL counts y from the bottom.
            gl.uniform2f(uCenter, centerX * ratio, height - centerY * ratio);
            gl.uniform1f(uScale, Math.max(1, radius * ratio));
            gl.uniform1f(uTime, seconds);
            gl.uniform1f(uEnergy, energy);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        },
        dispose: () => {
            gl.deleteTexture(texture);
            gl.deleteBuffer(quad);
            gl.deleteProgram(program);
            gl.deleteShader(vertex);
            gl.deleteShader(fragment);
        },
    };
};
